import { Vector2Int } from '../../core/math/vector2int.js';
import { RNG } from '../../core/random/rng.js';
import { GameBoard } from '../entities/gameboard.js';

const DIRECTIONS = Object.freeze([Vector2Int.right, Vector2Int.up]);
const PLACEMENT_STATE = Object.freeze({
  idle: 'idle',
  placing: 'placing',
  ready: 'ready',
  complete: 'complete',
});
const MAX_RANDOM_ATTEMPTS = 1000;

class PlacementSystem {
  #rng;
  #board;
  #ships;
  #state;
  #currentShipIndex;

  constructor() {
    this.reset();
  }

  get state() {
    return this.#state;
  }

  startPlacement(board, ships, seed) {
    if (this.#state !== PLACEMENT_STATE.idle) {
      throw new TypeError('Placement already in progress');
    }

    if (!(board instanceof GameBoard)) {
      throw new TypeError('Expects board to be an instance of GameBoard');
    }
    if (!Array.isArray(ships) || ships.length === 0) {
      throw new TypeError('Ships must be a non-empty array');
    }

    this.#rng = new RNG(seed);
    this.#board = board;
    this.#ships = ships;
    this.#state = PLACEMENT_STATE.placing;
    this.#currentShipIndex = 0;
  }

  resetPlacement() {
    if (this.#state === PLACEMENT_STATE.idle) {
      throw new Error('No active placement session');
    }

    this.#board.reset();
    this.#currentShipIndex = 0;
    this.#state = PLACEMENT_STATE.placing;
  }

  reset() {
    this.#rng = null;
    this.#board = null;
    this.#ships = null;
    this.#state = PLACEMENT_STATE.idle;
    this.#currentShipIndex = 0;
  }

  finalizePlacement() {
    if (this.#state === PLACEMENT_STATE.ready) {
      this.#state = PLACEMENT_STATE.complete;
      return true;
    }
    return false;
  }

  getCurrentShip() {
    if (this.#state === PLACEMENT_STATE.idle || this.#currentShipIndex === this.#ships.length) {
      return null;
    }
    const ship = this.#ships[this.#currentShipIndex];
    return { id: ship.id, length: ship.length };
  }

  getPlacementData(origin, direction) {
    if (this.#state === PLACEMENT_STATE.idle || this.#currentShipIndex === this.#ships.length) {
      return null;
    }

    const ship = this.#ships[this.#currentShipIndex];
    return {
      chain: this.#board.buildChainClamped(origin, direction, ship.length),
      canPlace: this.canPlace(origin, direction),
    };
  }

  canPlace(position, direction) {
    return (
      this.#state === PLACEMENT_STATE.placing &&
      this.#board.canPlaceShip(position, direction, this.#ships[this.#currentShipIndex].length)
    );
  }

  placeCurrentShip(position, direction, onPlace = (_chain) => {}) {
    if (this.canPlace(position, direction)) {
      if (typeof onPlace !== 'function') {
        throw new TypeError('Expects onPlace to be a function');
      }

      const ship = this.#ships[this.#currentShipIndex];
      const chain = this.#board.buildChain(position, direction, ship.length);
      this.#board.placeShip(ship, position, direction);
      this.#currentShipIndex++;

      if (this.getRemainingCount() === 0) {
        this.#state = PLACEMENT_STATE.ready;
      }

      onPlace?.(chain);
      return true;
    }

    return false;
  }

  autoPlaceRemaining(onPlace = (_chain) => {}) {
    if (this.#state !== PLACEMENT_STATE.placing) {
      return false;
    }
    while (this.#currentShipIndex < this.#ships.length) {
      let placed = false;
      let attempts = 0;
      while (!placed && attempts < MAX_RANDOM_ATTEMPTS) {
        attempts++;
        const direction = this.#rng.pick(DIRECTIONS);

        const length = this.#ships[this.#currentShipIndex].length;

        const [colMax, rowMax] = direction.equals(Vector2Int.right)
          ? [this.#board.width - length, this.#board.height]
          : [this.#board.width, this.#board.height - length];

        const col = this.#rng.nextInt(0, colMax);
        const row = this.#rng.nextInt(0, rowMax);
        const coord = new Vector2Int(col, row);

        if (this.placeCurrentShip(coord, direction, onPlace)) {
          placed = true;
        }
      }
      if (!placed) throw new Error('Failed to place ship');
    }

    return true;
  }

  autoPlaceAll(onPlace = (_chain) => {}) {
    if (this.#state !== PLACEMENT_STATE.placing && this.#state !== PLACEMENT_STATE.ready) {
      return false;
    }
    this.resetPlacement();
    return this.autoPlaceRemaining(onPlace);
  }

  isPlacementComplete() {
    return this.#state === PLACEMENT_STATE.complete;
  }

  getRemainingCount() {
    if (this.#currentShipIndex >= this.#ships.length) {
      return 0;
    }
    return this.#ships.length - this.#currentShipIndex;
  }

  getPlacementProgress() {
    if (this.#state === PLACEMENT_STATE.idle) {
      return null;
    }
    return {
      state: this.#state,
      totalShips: this.#ships.length,
      currentShipIndex: this.#currentShipIndex,
      remainingCount: this.getRemainingCount(),
    };
  }
}

export { PlacementSystem };
